/**
 * Helpers for the RFID card expiry date (valid_until).
 *
 * valid_until is exchanged with the backend as an ISO 8601 UTC string,
 * or null when the card has no expiry.
 */

/** Label shown when a card has no expiry date. */
export const NO_EXPIRY_LABEL = "No expiry";

// valid_until is a UTC instant; format it in UTC so the displayed calendar day
// matches the contract regardless of the operator's local timezone.
const expiryFormatter = new Intl.DateTimeFormat("en-GB", {
	timeZone: "UTC",
	day: "2-digit",
	month: "short",
	year: "numeric",
});

/** True when the card has a concrete expiry value (not null/empty). */
export function hasExpiry(validUntil: string | null | undefined): boolean {
	return validUntil != null && validUntil !== "";
}

/** True when the card's expiry is in the past. Cards with no expiry are never expired. */
export function isExpired(validUntil: string | null | undefined): boolean {
	if (!hasExpiry(validUntil)) return false;
	const time = new Date(validUntil as string).getTime();
	if (Number.isNaN(time)) return false;
	return time < Date.now();
}

/**
 * Human-readable expiry for the list, e.g. "31 Dec 2025".
 * Returns NO_EXPIRY_LABEL when there is no expiry, and falls back to the raw
 * value if it cannot be parsed (degrade gracefully rather than crash).
 */
export function formatExpiry(validUntil: string | null | undefined): string {
	if (!hasExpiry(validUntil)) return NO_EXPIRY_LABEL;
	const date = new Date(validUntil as string);
	if (Number.isNaN(date.getTime())) return NO_EXPIRY_LABEL;
	return expiryFormatter.format(date);
}

/**
 * Serialize a Calendar Date (or null) to an ISO 8601 UTC string, or null for no expiry.
 *
 * The Calendar gives a Date at local midnight for the calendar day the operator
 * selected. We treat that day as expiring at the end of the day in UTC
 * (23:59:59Z), matching the backend contract's `…T23:59:59Z` form and avoiding a
 * timezone day-shift (a naive toISOString() would move the instant to the
 * previous day for operators east of UTC).
 */
export function dateToIsoUtc(date: Date | null | undefined): string | null {
	if (!date || Number.isNaN(date.getTime())) return null;
	const endOfDayUtc = new Date(
		Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59)
	);
	return endOfDayUtc.toISOString();
}

/**
 * Parse an ISO 8601 string into a Date for the Calendar, or null when absent/invalid.
 *
 * Maps the UTC instant back to a local-midnight Date for the same UTC calendar
 * day, so the Calendar pre-fills the day the operator originally picked rather
 * than shifting it by the local offset.
 */
export function isoToDate(iso: string | null | undefined): Date | null {
	if (!hasExpiry(iso)) return null;
	const date = new Date(iso as string);
	if (Number.isNaN(date.getTime())) return null;
	return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}
